import { DownOutlined, EditOutlined, PlusOutlined, StarFilled, StarOutlined, DeleteOutlined } from "@ant-design/icons"
import { Flex, Alert, Tooltip, Modal, Button, Form, Popover, Input, Checkbox, Select } from "antd"
import { Fragment, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FORM_LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from "../../../utils/formTranslations.js"

const compactViewTranslations = (map = {}) => {
    const result = {}
    Object.entries(map).forEach(([lng, bucket]) => {
        if (!bucket || typeof bucket !== "object") return
        const name = typeof bucket.name === "string" ? bucket.name.trim() : ""
        if (name) result[lng] = { name }
    })
    return result
}

const ViewSelector = (props) => {
    const { views, selectedView, handleViewChange, saveAsDefaultView, userInfo, spaceEntitlements, triggerSaveModal, onSaveModalTriggered, availableViews } = props
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();
    const [editView, setEditView] = useState({})
    const [popoverVisible, setPopoverVisible] = useState(false)
    const [editLang, setEditLang] = useState(DEFAULT_LANGUAGE)
    const [translations, setTranslations] = useState({})
    const isDefaultLang = editLang === DEFAULT_LANGUAGE

    const getViewDisplayName = (view) => {
        if (!view) return ""
        const translated = view.translations?.[i18n.language]?.name
        return (typeof translated === "string" && translated.trim()) ? translated : (view.label || view.name || "")
    }

    const resetTranslationState = (nextTranslations = {}) => {
        setEditLang(DEFAULT_LANGUAGE)
        setTranslations(nextTranslations && typeof nextTranslations === "object" ? { ...nextTranslations } : {})
    }

    const setTranslationField = (key, val) => {
        setTranslations(prev => ({
            ...prev,
            [editLang]: { ...(prev[editLang] || {}), [key]: val },
        }))
    }

    useEffect(() => {
        if (selectedView) {
            form.setFieldValue({ name: selectedView.name, shareWithUsers: selectedView.shareWithUsers })
        }
    }, [selectedView, form])

    // Effect to trigger save modal from parent component
    useEffect(() => {
        if (triggerSaveModal) {
            setEditView({}); // Clear any existing edit view (so it's a new save)
            resetTranslationState()
            form.resetFields();
            setNewViewModal(true);
            // Call callback to reset the trigger in parent
            if (onSaveModalTriggered) {
                onSaveModalTriggered();
            }
        }
    }, [triggerSaveModal, onSaveModalTriggered])

    const [newViewModal, setNewViewModal] = useState(false)

    const closeViewModal = () => {
        setNewViewModal(false)
        setEditView({})
        resetTranslationState()
        form.resetFields();
    }

    const saveView = (values) => {
        const canonicalName = (values.name || editView.name || "").trim()
        if (!canonicalName) {
            setEditLang(DEFAULT_LANGUAGE)
            form.setFields([{ name: "name", errors: [t('View name is required')] }])
            return
        }
        let isNew = editView.viewId ? false : true
        let viewObj = {
            ...values,
            name: canonicalName,
            translations: compactViewTranslations(translations),
        }
        if (!isNew) {
            viewObj.viewId = editView.viewId
        }
        props.saveDocView(viewObj, isNew)
        closeViewModal()
    }

    const handleEditView = (e, view) => {
        e.preventDefault();
        e.stopPropagation();
        // handleViewChange(view)
        setEditView({ ...view })
        resetTranslationState(view.translations || {})
        form.setFieldsValue({ viewId: view.viewId, name: view.name, shareWithUsers: view.shareWithUsers, sharedWithUsers: view.sharedWithUsers })
        setNewViewModal(true)
        setPopoverVisible(false); // Close popover when editing view
    }

    const handleDeleteView = (e, view) => {
        e.preventDefault();
        e.stopPropagation();
        setPopoverVisible(false); // Close popover when showing delete confirmation
        Modal.confirm({
            title: t('Confirm'),
            content: t("Are you sure to delete the view?"),
            okText: t("Yes"),
            onOk: (event) => {
                props.deleteDocumentView(view)
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const createNewView = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setEditView({})
        resetTranslationState()
        form.resetFields();
        setNewViewModal(true);
        setPopoverVisible(false); // Close popover when creating new view
    }

    const handleViewSelect = (view) => {
        handleViewChange(view);
        setPopoverVisible(false); // Close popover after selecting a view
    }

    const handleSaveAsDefault = (e, view) => {
        e.preventDefault();
        e.stopPropagation();
        saveAsDefaultView(e, view);
        setPopoverVisible(false); // Close popover after saving as default
    }

    return (
        <Fragment>
            <Popover
                className="view-selector-btn"
                trigger="click"
                placement="bottomRight"
                open={popoverVisible}
                onOpenChange={setPopoverVisible}
                content={
                    <div className="view-selector">
                        {
                            (availableViews || views).map(view => {
                                return (
                                    <Flex key={view.value} justify="space-between" className={`view-selection-row ${(view.value && selectedView?.value && String(view.value) === String(selectedView.value)) || (view.label && selectedView?.label && view.label === selectedView.label) ? 'active' : ''}`} align="center" onClick={() => handleViewSelect(view)}>
                                        <div className="template-title">{getViewDisplayName(view)}</div>
                                        <Flex justify="flex-end" align="center" className="view-selector-icon">
                                            {/* {
                                                (!view.isDefault && view?.updatedBy === userInfo.userId && spaceEntitlements.includes("Workflow_Documents_View_Update")) &&
                                                <EditOutlined style={{ fontSize: "12px", marginRight: "8px" }} onClick={(e) => handleEditView(e, view)} />
                                            } */}
                                            {
                                                (!view.default && (userInfo.isAdmin || (view.updatedBy === userInfo.userId && spaceEntitlements.includes("Workflow_Documents_View_Update")))) ?
                                                    <>
                                                        <EditOutlined style={{ fontSize: "12px", marginRight: "8px" }} onClick={(e) => handleEditView(e, view)} />
                                                        <DeleteOutlined style={{ fontSize: "12px", marginRight: "8px" }} onClick={(e) => handleDeleteView(e, view)} />
                                                    </>
                                                    : null
                                            }
                                            {
                                                view.defaultViewers && view.defaultViewers.includes(userInfo.userId) ?
                                                    <Tooltip title={t("Saved as Default")} placement="right">
                                                        <StarFilled style={{ color: "blue", fontSize: "12px" }} />

                                                    </Tooltip>
                                                    :
                                                    <Tooltip title={t("Save as Default")} placement="right">
                                                        <StarOutlined style={{ fontSize: "12px" }} onClick={(e) => handleSaveAsDefault(e, view)} />
                                                    </Tooltip>
                                            }
                                        </Flex>
                                    </Flex>

                                )
                            })

                        }
                        {
                            spaceEntitlements.includes("Workflow_Documents_View_Create") &&
                            <Flex justify="space-between" className="last-row view-selection-row" style={{ borderTop: "1px solid #f5f5f5", borderRadius: '0' }} onClick={createNewView}>
                                <div className="template-title">{t("Save View as New")}</div>
                                <PlusOutlined style={{ fontSize: "12px" }} />
                            </Flex>
                        }
                    </div>
                }>
                <Button style={{ display: "flex", alignItems: "center" }}>{getViewDisplayName(selectedView)} {<DownOutlined />}</Button>

            </Popover>
            {/* <Select onChange={handleViewChange} value={selectedView} className="view-selector">
                {
                    views.map(view => {
                        return (
                            <Select.Option key={view.value} value={view.value}>
                                <Flex justify="space-between" className="view-selection-row" align="center">
                                    <div className="template-title">{view.label}</div>
                                    <Flex justify="flex-end" align="center" className="view-selector-icon">
                                        <EditOutlined style={{ fontSize: "12px", marginRight: "8px" }} />
                                        {
                                            view.isDefault ?
                                                <StarFilled style={{ color: "blue", fontSize: "12px" }} />
                                                :
                                                <Tooltip title="Save as Default" placement="right">
                                                    <StarOutlined style={{ fontSize: "12px" }} onClick={() => saveAsDefaultView(view.value)} />
                                                </Tooltip>
                                        }
                                    </Flex>
                                </Flex>
                            </Select.Option>
                        )
                    })
                }
                <Flex justify="space-between" className="last-row view-selection-row" style={{ borderTop: "1px solid #f5f5f5", borderRadius: '0'}} onClick={() => setNewViewModal(true)}>
                    <div className="template-title">Save View As New</div>
                    <PlusOutlined style={{ fontSize: "12px" }} />
                </Flex>
            </Select> */}

            {/* New View Modal */}
            <Modal title={editView.viewId ? t("Save View") : t("Save View as New")} open={newViewModal} footer={false} onOk={() => setNewViewModal(false)} onCancel={closeViewModal}>
                <Alert
                    message={t("Saved view will have selected columns and filters")}
                    description=""
                    type="info"
                    showIcon
                    style={{
                        width: "100%",
                        textAlign: "left",
                        background: "#e6f4ff",
                        borderColor: "#91CAFF",
                        padding: "8px 12px",
                        marginBottom: "16px"
                    }}
                />
                <Form
                    name="basic"
                    layout="vertical"
                    form={form}
                    onFinish={saveView}
                    onFinishFailed={() => {
                        if (!isDefaultLang) setEditLang(DEFAULT_LANGUAGE)
                    }}
                >
                    <Form.Item style={{ marginBottom: "16px" }}>
                        <Select
                            value={editLang}
                            onChange={setEditLang}
                            options={FORM_LANGUAGE_OPTIONS}
                        />
                        {!isDefaultLang && (
                            <div className="text-color-secondary" style={{ marginTop: 4, fontSize: 12 }}>
                                {t("Editing translation — only the view name is saved for this language. Other settings are shared across languages.")}
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item
                        label={isDefaultLang ? t("View Name") : undefined}
                        name="name"
                        hidden={!isDefaultLang}
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                                message: t('View name is required'),
                            },
                        ]}
                        style={{ marginBottom: isDefaultLang ? "16px" : 0 }}
                    >
                        <Input />
                    </Form.Item>
                    {!isDefaultLang && (
                        <Form.Item label={t("View Name")} style={{ marginBottom: "16px" }}>
                            <Input
                                value={translations[editLang]?.name || ""}
                                onChange={(e) => setTranslationField("name", e.target.value)}
                            />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="sharedWithUsers"
                        valuePropName="checked"
                        wrapperCol={{
                            span: 16,
                        }}
                        style={{ marginBottom: "16px" }}
                    >
                        <Checkbox>{t("Share with all users in this space")}</Checkbox>
                    </Form.Item>
                    <Form.Item style={{ margin: "0" }}>
                        <Flex justify="flex-end" gap={8} align="center" style={{ paddingBottom: "8px" }}>
                            <Button onClick={closeViewModal}>{t("Cancel")}</Button>
                            <Button type="primary" htmlType="submit">{editView.viewId ? t("Update") : t("Submit")}</Button>
                        </Flex>
                    </Form.Item>
                </Form>
            </Modal>
        </Fragment>
    )
}

export default ViewSelector